import { useStrapiClient } from "@xs2event/js-lib/composables";
import { useQuery } from "@tanstack/vue-query";
import { type Brand } from "@/brandconfig";
import qs from "qs";
import { computed } from "vue";

const THIRTY_MINUTES = 30 * 60 * 1000;

const collectionName = "brands";

const HOME_BLOCKS_SECTION = "homepageBlocks";
const FEATURED_EVENTS = "featuredEvents"; // Home Page featured Events

export interface FeaturedEvent {
  id: number;
  title: string;
  description: string;
  image: StrapiImage;
  url: string;
}

export const useGetBrandHomePageSections = (brand: Brand) => {
  const client = useStrapiClient();
  const query = qs.stringify(
    {
      filters: {
        brand: {
          $eq: brand,
        },
        locale: "en",
      },
      populate: {
        [HOME_BLOCKS_SECTION]: {
          populate: {
            image: {
              fields: ["url", "formats"],
            },
          },
        },
      },
    },
    {
      encodeValuesOnly: true,
    }
  );

  return useQuery({
    queryKey: [HOME_BLOCKS_SECTION, brand],
    queryFn: async () => {
      const response = await client.get(`${collectionName}?${query}`);
      return response.data;
    },
    staleTime: THIRTY_MINUTES, // 30 minutes
    retry: false,
  });
};

export const useGetHomePageFeaturedEvents = (brand: Brand) => {
  const client = useStrapiClient();
  return useQuery<FeaturedEvent[]>({
    queryKey: computed(() => [FEATURED_EVENTS, brand]),
    queryFn: async () => {
      const query = qs.stringify(
        {
          filters: {
            brand: {
              $eq: brand,
            },
            //Hardcoded locale for now
            locale: "en",
          },
          populate: {
            [FEATURED_EVENTS]: {
              populate: {
                image: {
                  fields: ["url", "formats"],
                },
              },
            },
          },
        },
        { encodeValuesOnly: true }
      );
      const response = await client.get(`${collectionName}?${query}`);

      console.log("useGetHomePageFeaturedEvents response", response, response.data.data[0]);

      return response.data.data[0].featuredEvents ?? [];
    },

    staleTime: THIRTY_MINUTES,
    retry: false,
  });
};

export const useFeaturedSports = (brand: Brand) => {
  const client = useStrapiClient();
  const query = qs.stringify(
    {
      fields: [],
      filters: {
        brand: {
          $eq: brand,
        },
        locale: "en",
      },
      populate: {
        featuredSports: {
          fields: ["sport"],
        },
      },
      pagination: {
        page: 1,
        pageSize: 1,
      },
    },
    {
      encodeValuesOnly: true,
    }
  );

  return useQuery<string[]>({
    queryKey: ["featuredSports", brand],
    queryFn: async () => {
      const response = await client.get(`${collectionName}?${query}`);

      return response.data.data[0]?.featuredSports?.map((x) => x.sport) || [];
    },
    throwOnError: true,
    staleTime: THIRTY_MINUTES, // 30 minutes
    retry: false,
  });
};

type StrapiImageFormat = { url?: string };
type StrapiImage = {
  url?: string;
  formats?: {
    small?: StrapiImageFormat;
    thumbnail?: StrapiImageFormat;
  };
};

type HomePageBlock = {
  image?: StrapiImage;
  title?: string;
  url?: string;
  description?: string;
  tag?: string;
  type?: string;
};

export const normalizeHomePageBlocks = (strapiData: unknown) => {
  const blocksUnknown = (strapiData as { data?: Array<Record<string, unknown>> | undefined })?.data?.[0]?.[
    HOME_BLOCKS_SECTION
  ];
  const blocks = Array.isArray(blocksUnknown) ? (blocksUnknown as HomePageBlock[]) : [];

  const blockMap: { featured: Array<Partial<Record<string, string>>> } = {
    featured: [],
  };

  blocks.forEach((block: HomePageBlock) => {
    blockMap.featured.push({
      photo: block.image?.formats?.medium?.url || block.image?.url || "",
      altText: block.title || "",
      to: block.url || "",
      title: block.title || "",
      description: block.description || "",
      tag: block.tag || "",
      type: block.type || "",
    });
  });

  return blockMap;
};
